<script setup lang="ts">
import type { AgentPortfolioSectionProps } from '.';

import { useCarouselTracking } from '~/composables/events/useCarouselTracking';
import { useEvents } from '~/composables/events/useEvents';
import { useAgentPortfolio } from '~/composables/useAgentPortfolio';
import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { buildCarouselTrackingContext } from '~/composables/useListingEventTracking';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { cn } from '~/utils';

import { PropertyCard } from '~/components/PropertyCard';
import { StandardSlider } from '~/components/StandardSlider';
import { Button } from '~/components/ui/button';

import {
	AGENT_PORTFOLIO_CARD_FOOTER_CLASS,
	AGENT_PORTFOLIO_ITEM_CLASS,
	AGENT_PORTFOLIO_SECTION_CLASS,
	AGENT_PORTFOLIO_VIEWPORT_CLASS
} from './layout';
import SectionSkeleton from './SectionSkeleton.vue';

const props = defineProps<AgentPortfolioSectionProps>();

const { properties, total, isLoading, seeAllFilters } = useAgentPortfolio(
	() => props.agentSlug
);
const sectionName = 'agent profile portfolio';

const { trackCtaViewMoreClicked } = useEvents();
const { trackItemClicked, trackNavigated } = useCarouselTracking(
	'agent_properties',
	sectionName
);
const route = useRoute();
const { ft_srp_agent_filter: isAgentFilterEnabled } = useFeatureFlags();

const seeAllHref = computed(() =>
	isAgentFilterEnabled && props.agentSlug
		? PropertyFiltersPresenter.getPathFromFilters(seeAllFilters.value)
		: ''
);
const formattedTotal = computed(() => total.value.toLocaleString('pt-BR'));
const trackingContext = computed(() =>
	buildCarouselTrackingContext({
		placement: 'corretor_portfolio',
		properties: properties.value
	})
);

function handleViewAllClick() {
	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: sectionName,
		[MixpanelProps.DESTINATION_URL]: seeAllHref.value,
		[MixpanelProps.CTA_TEXT]: 'Ver todos'
	});
}
const hasContent = computed<boolean>(() => properties.value.length > 0);

defineExpose({ hasContent });
</script>

<template>
	<SectionSkeleton v-if="isLoading" :class="props.class" />

	<section
		v-else-if="properties.length"
		:class="cn(AGENT_PORTFOLIO_SECTION_CLASS, props.class)"
		aria-labelledby="agent-portfolio-title"
		data-testid="agent-portfolio-section"
	>
		<StandardSlider
			:items="properties"
			:is-loading="isLoading"
			:tracking-context="trackingContext"
			:section-name="sectionName"
			class="overflow-visible!"
			container-class="p-0!"
			carousel-item-size="full"
			:carousel-item-class="AGENT_PORTFOLIO_ITEM_CLASS"
			carousel-content-class="gap-5!"
			:carousel-viewport-class="AGENT_PORTFOLIO_VIEWPORT_CLASS"
			:carousel-options="{ class: 'gap-6!' }"
			:breakpoints="{
				'(min-width: 640px)': { slidesToScroll: 2 },
				'(min-width: 1024px)': { slidesToScroll: 1 },
				'(min-width: 1280px)': { slidesToScroll: 2 }
			}"
			footer-class="min-h-[104px] gap-6 max-md:[&>div:first-child]:w-full md:min-h-12 md:flex-row md:items-center"
			@navigate="trackNavigated"
		>
			<template #header>
				<header class="pb-6">
					<h2
						id="agent-portfolio-title"
						class="font-matter-sq text-2xl font-normal leading-[150%] tracking-[-0.03rem] text-primary lg:text-[32px] lg:tracking-[-0.04rem]"
					>
						Portfólio de imóveis
					</h2>
				</header>
			</template>

			<template #items="{ item, index }">
				<PropertyCard
					:property="item"
					:section="sectionName"
					:card-position="index"
					:tracking-context="trackingContext"
					:position-in-list="index"
					class="md:border-0 md:ring-1 md:ring-inset md:ring-white-dark-3"
					:footer-class="AGENT_PORTFOLIO_CARD_FOOTER_CLASS"
					@card-click="trackItemClicked(item, index)"
				/>
			</template>

			<template #cta>
				<Button
					v-if="!isLoading && seeAllHref"
					as-child
					variant="secondary"
					class="h-12 w-full shrink-0 gap-3 rounded-none px-5 py-3 md:w-auto"
				>
					<NuxtLink
						:to="seeAllHref"
						data-testid="agent-portfolio-view-all"
						@click="handleViewAllClick"
					>
						<span>Ver todos</span>
						<span
							class="rounded-full bg-white-dark-1 px-1 text-xs font-medium leading-5"
						>
							{{ formattedTotal }}
						</span>
					</NuxtLink>
				</Button>
			</template>
		</StandardSlider>
	</section>
</template>
